/**
 * ComparePicker — searchable multi-select popover for choosing saved
 * analyses to overlay on the Growth chart.
 *
 * The Spreadsheet Growth Comparison chart previously exposed only the
 * five most-recent records as chip buttons — a hard ceiling that
 * hid older cycles from comparison. This component replaces that chip
 * list with a full-list dropdown so admins can pick ANY saved record
 * (with type-ahead search when the list is long) up to the same
 * hard-coded overlay limit of 5.
 *
 * Props:
 *   allRecords      — full spreadsheet-records list from the parent
 *   activeRecordId  — the record currently open (excluded from picker)
 *   selected        — the current comparisonAnalyses array
 *   setSelected     — setter for comparisonAnalyses
 */
import { useMemo, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const MAX_COMPARE = 5;

export default function ComparePicker({ allRecords, activeRecordId, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const candidates = useMemo(
    () => (allRecords || []).filter((r) => r.id !== activeRecordId),
    [allRecords, activeRecordId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((r) =>
      (r.name || '').toLowerCase().includes(q)
      || (r.species || '').toLowerCase().includes(q),
    );
  }, [candidates, query]);

  const toggle = (rec) => {
    const isSelected = selected.some((c) => c.id === rec.id);
    if (isSelected) {
      setSelected((prev) => prev.filter((c) => c.id !== rec.id));
    } else if (selected.length < MAX_COMPARE) {
      setSelected((prev) => [...prev, rec]);
    }
  };

  if (candidates.length === 0) {
    return <p className="text-xs text-gray-500 italic">No other saved analyses available</p>;
  }

  const summary = selected.length === 0
    ? `Pick from ${candidates.length} saved analyses…`
    : `${selected.length} selected · pick more (max ${MAX_COMPARE})`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between text-xs px-3 py-2 rounded-md border border-purple-300 bg-white hover:border-purple-400 hover:bg-purple-50/40 transition-colors"
          data-testid="compare-picker-trigger"
        >
          <span className="text-gray-700 truncate">{summary}</span>
          <ChevronDown className="w-3.5 h-3.5 text-purple-600 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="px-2 pt-2 pb-1 border-b border-slate-100 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or species"
            className="flex-1 text-xs bg-transparent outline-none py-1"
            data-testid="compare-picker-search"
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1" data-testid="compare-picker-list">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-400 italic">
              Nothing matches “{query}”.
            </div>
          )}
          {filtered.map((rec) => {
            const isSelected = selected.some((c) => c.id === rec.id);
            const capped = !isSelected && selected.length >= MAX_COMPARE;
            return (
              <button
                key={rec.id}
                type="button"
                disabled={capped}
                onClick={() => toggle(rec)}
                className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                  isSelected
                    ? 'bg-purple-50 text-purple-800'
                    : capped
                      ? 'text-slate-400 cursor-not-allowed'
                      : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`compare-picker-option-${rec.id}`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                  isSelected ? 'border-purple-500 bg-purple-500 text-white' : 'border-slate-300'
                }`}>
                  {isSelected && <Check className="w-2.5 h-2.5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{rec.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {rec.species || '—'}
                    {rec.data_points?.length ? ` · ${rec.data_points.length} days` : ''}
                    {rec.updated_at ? ` · ${new Date(rec.updated_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-500 flex justify-between">
          <span>{filtered.length} of {candidates.length}</span>
          <span>{selected.length} / {MAX_COMPARE} selected</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
